import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { CitationSource, ResourceLink, isSpreadsheetSource, isWebSource } from '../types';
import { Citation, SpreadsheetCitation, WebCitation } from './Citation';
import { ResourceLink as ResourceLinkComponent } from './ResourceLink';
import { MarkdownImage } from './MarkdownImage';
import { prepareChatMarkdown } from '../utils/markdown';

import { visit } from 'unist-util-visit';
import Tooltip from '@/components/ui/Tooltip';

export function citationPlugin() {
  return (tree: any) => {
    visit(tree, 'text', (node: any, index: any, parent: any) => {
      const regex = /\[CITATION:(\d+)(_[a-z]+)?\]/g;
      const value = node.value;

      let match;
      const newNodes: any[] = [];
      let lastIndex = 0;
      let hasCitations = false;

      while ((match = regex.exec(value)) !== null) {
        hasCitations = true;
        const before = value.slice(lastIndex, match.index);
        if (before) newNodes.push({ type: 'text', value: before });

        const citationIndex = match[1];
        newNodes.push({
          type: 'citation',
          data: {
            hName: 'citation',
            hProperties: { id: citationIndex }
          }
        });

        lastIndex = regex.lastIndex;
      }

      if (!hasCitations) return;

      const after = value.slice(lastIndex);
      if (after) newNodes.push({ type: 'text', value: after });

      const resultNodes: any[] = [];
      let currentGroup: any[] = [];

      const pushCurrentGroup = () => {
        const citationsInGroup = currentGroup.filter((n) => n.type === 'citation');

        if (citationsInGroup.length > 1) {
          // Keep first two citations and any text between them
          let citationsAdded = 0;
          let i = 0;
          for (; i < currentGroup.length; i++) {
            resultNodes.push(currentGroup[i]);
            if (currentGroup[i].type === 'citation') {
              citationsAdded++;
            }
            if (citationsAdded === 1) {
              i++;
              break;
            }
          }

          // Group the rest into citationMore (ignoring any whitespace between them)
          const remainingCitations = currentGroup.slice(i).filter((n) => n.type === 'citation');
          if (remainingCitations.length > 0) {
            resultNodes.push({
              type: 'citationMore',
              data: {
                hName: 'citationMore',
                hProperties: {
                  ids: remainingCitations.map((n) => n.data.hProperties.id)
                }
              }
            });
          }
        } else {
          resultNodes.push(...currentGroup);
        }
        currentGroup = [];
      };

      for (const n of newNodes) {
        if (n.type === 'citation') {
          currentGroup.push(n);
        } else if (n.type === 'text' && n.value.trim() === '' && currentGroup.length > 0) {
          // If it's whitespace and we're currently building a citation group, add it to the group
          currentGroup.push(n);
        } else {
          pushCurrentGroup();
          resultNodes.push(n);
        }
      }
      pushCurrentGroup();

      if (resultNodes.length > 0) {
        parent.children.splice(index, 1, ...resultNodes);
      }
    });
  };
}

/**
 * Remark plugin that rewrites `[RESOURCE_START:<ulid>]label[RESOURCE_END]`
 * markers (emitted by {@link useChatStream} for `resource_start`/`resource_end`
 * stream parts — and for legacy `file_link_*` parts, which the hook
 * normalises into the same shape) into `resourceLink` AST nodes consumed by
 * the markdown renderer below. The resolved `ResourceLink` record is looked
 * up on render from `message.resource_links`.
 */
export function resourceLinkPlugin() {
  return (tree: any) => {
    visit(tree, 'text', (node: any, index: any, parent: any) => {
      const regex = /\[RESOURCE_START:([^\]]+)\](.*?)\[RESOURCE_END\]/g;
      const value = node.value;

      let match;
      const newNodes: any[] = [];
      let lastIndex = 0;

      while ((match = regex.exec(value)) !== null) {
        const before = value.slice(lastIndex, match.index);
        if (before) newNodes.push({ type: 'text', value: before });

        const resourceId = match[1];
        const content = match[2] || resourceId;
        newNodes.push({
          type: 'resourceLink',
          data: {
            hName: 'resourceLink',
            hProperties: { resourceId, content }
          }
        });

        lastIndex = regex.lastIndex;
      }

      const after = value.slice(lastIndex);
      if (after) newNodes.push({ type: 'text', value: after });

      if (newNodes.length > 0) {
        parent.children.splice(index, 1, ...newNodes);
      }
    });
  };
}

function citationMoreButtonClassName(citations: CitationSource[], moreIds: string[]): string {
  // Plugin shows the first citation in the group, then "+N" for the rest — match that first pill.
  const firstShownIndex = parseInt(moreIds[0], 10) - 2;
  const anchor = citations[firstShownIndex];
  const base = 'inline-flex items-center text-content-default text-xs rounded-full align-top ml-0.5 h-5 max-w-[200px]';
  if (anchor && isWebSource(anchor)) {
    return `${base} bg-gray-200 hover:bg-gray-300`;
  }
  if (anchor) {
    return `${base} bg-blue-200 hover:bg-blue-100`;
  }
  return `${base} bg-gray-200 hover:bg-gray-300`;
}

function CitationMore({
  ids,
  citations,
  onViewSourceClick,
  hideCitationTitle = false
}: {
  ids: string[];
  citations: CitationSource[];
  onViewSourceClick: (source: CitationSource) => void;
  hideCitationTitle?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const morePillClass = citationMoreButtonClassName(citations, ids);

  return (
    <>
      <span className={isExpanded ? 'contents' : 'hidden'}>
        {ids.map((id) => {
          const data = citations?.[parseInt(id, 10) - 1];
          if (!data) {
            return null;
          }
          if (isWebSource(data)) {
            return <WebCitation key={id} data={data} />;
          }
          if (isSpreadsheetSource(data)) {
            return (
              <SpreadsheetCitation
                key={id}
                data={data}
                onViewSourceClick={onViewSourceClick}
                hideTitle={hideCitationTitle}
              />
            );
          }
          return <Citation key={id} data={data} onViewSourceClick={onViewSourceClick} hideTitle={hideCitationTitle} />;
        })}
      </span>
      <Tooltip title={isExpanded ? 'Hide extra citations' : `Show ${ids.length} more citations`}>
        <button
          onClick={() => setIsExpanded((open) => !open)}
          className={`${morePillClass} ${isExpanded ? 'px-2' : 'px-3'}`}
        >
          {isExpanded ? 'Hide' : `+${ids.length}`}
        </button>
      </Tooltip>
    </>
  );
}

export function MarkdownWithCitations({
  markdown,
  citations,
  resourceLinks,
  onViewSourceClick,
  onImageClick,
  hideCitationTitle = false
}: {
  markdown: string;
  citations: CitationSource[];
  resourceLinks?: ResourceLink[];
  onViewSourceClick: (source: CitationSource) => void;
  onImageClick?: (url: string, alt?: string) => void;
  hideCitationTitle?: boolean;
}) {
  // TODO: make sure consistent plugins and styles across all uses of ReactMarkdown
  const displayMarkdown = prepareChatMarkdown(markdown);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkMath, citationPlugin, resourceLinkPlugin, remarkGfm]}
      rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }]]}
      components={
        {
          table: ({ children, ...props }: any) => (
            <div className="overflow-x-auto custom-scrollbar">
              <table {...props}>{children}</table>
            </div>
          ),
          citation: ({ node }: { node: any }) => {
            const id = node.properties.id;
            const data = citations?.[parseInt(id, 10) - 1];
            if (!data) {
              return null;
            }
            if (isWebSource(data)) {
              return <WebCitation data={data} />;
            }
            if (isSpreadsheetSource(data)) {
              return (
                <SpreadsheetCitation data={data} onViewSourceClick={onViewSourceClick} hideTitle={hideCitationTitle} />
              );
            }
            return <Citation data={data} onViewSourceClick={onViewSourceClick} hideTitle={hideCitationTitle} />;
          },
          citationMore: ({ node }: { node: any }) => {
            const ids = node.properties.ids;
            return (
              <CitationMore
                ids={ids}
                citations={citations}
                onViewSourceClick={onViewSourceClick}
                hideCitationTitle={hideCitationTitle}
              />
            );
          },
          resourceLink: ({ node }: { node: any }) => {
            const resourceId = node.properties.resourceId;
            const content = node.properties.content;
            const data = resourceLinks?.find((rl) => rl.id === resourceId);

            if (!data) {
              // We received the marker but not the metadata (e.g. legacy
              // message or stream ordering issue) — fall back to the raw
              // label so the text still reads correctly.
              return <span>{content}</span>;
            }

            return <ResourceLinkComponent content={content} data={data} />;
          },
          img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
            <MarkdownImage {...props} onImageClick={onImageClick} />
          )
        } as any
      }
    >
      {displayMarkdown}
    </ReactMarkdown>
  );
}
